import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Download, XCircle } from "lucide-react";

export interface LightboxPhoto {
    src: string;
    alt?: string;
    /** Shown under the photo — which device it shows ("2\" Double Check · SN 40613G · Tag photo"). */
    caption?: string;
    /** Same-origin URL that saves the photo under its human name. */
    downloadHref?: string;
}

/**
 * Full-screen image viewer — safe in standalone PWA (no new tab).
 *
 * Rendered through a portal to document.body: several mount points sit inside
 * cards whose backdrop-filter creates a containing block, which would trap the
 * fixed-position overlay in the card's stacking context (it painted between
 * cards, under the sticky job header). The portal + z-50 keeps it above
 * everything.
 *
 * Single image: pass `src`/`alt`. Multi-photo: pass `photos` (+ optional
 * `initialIndex`) to get ←/→ keyboard + on-screen navigation. Esc or a
 * backdrop click closes.
 */
export function ImageLightbox({
    src,
    alt,
    photos,
    initialIndex = 0,
    onClose,
}: {
    src?: string;
    alt?: string;
    photos?: LightboxPhoto[];
    initialIndex?: number;
    onClose: () => void;
}) {
    const list: LightboxPhoto[] = photos && photos.length > 0 ? photos : src ? [{ src, alt }] : [];
    const [index, setIndex] = useState(() => Math.min(Math.max(initialIndex, 0), Math.max(list.length - 1, 0)));
    const many = list.length > 1;

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
            else if (many && e.key === "ArrowLeft") setIndex((i) => (i - 1 + list.length) % list.length);
            else if (many && e.key === "ArrowRight") setIndex((i) => (i + 1) % list.length);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [many, list.length, onClose]);

    if (list.length === 0) return null;
    const current = list[index];

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label={current.alt ?? "Photo viewer"}
        >
            <button
                type="button"
                className="absolute top-4 right-4 text-white/80 hover:text-white z-10"
                onClick={onClose}
                aria-label="Close"
            >
                <XCircle className="h-8 w-8" />
            </button>
            {many && (
                <>
                    <button
                        type="button"
                        className="absolute left-2 md:left-4 text-white/80 hover:text-white z-10 p-2"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIndex((i) => (i - 1 + list.length) % list.length);
                        }}
                        aria-label="Previous photo"
                    >
                        <ChevronLeft className="h-9 w-9" />
                    </button>
                    <button
                        type="button"
                        className="absolute right-2 md:right-4 text-white/80 hover:text-white z-10 p-2"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIndex((i) => (i + 1) % list.length);
                        }}
                        aria-label="Next photo"
                    >
                        <ChevronRight className="h-9 w-9" />
                    </button>
                </>
            )}
            {(many || current.caption || current.downloadHref) && (
                <div
                    className="absolute bottom-4 left-1/2 flex max-w-[92vw] -translate-x-1/2 items-center gap-2 rounded-full bg-black/60 px-3 py-1 text-xs text-white/90"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="lightbox-caption"
                >
                    <span className="truncate">
                        {(current.caption ?? current.alt) ? `${current.caption ?? current.alt}` : ""}
                        {many ? `${(current.caption ?? current.alt) ? " · " : ""}${index + 1} / ${list.length}` : ""}
                    </span>
                    {current.downloadHref && (
                        <a
                            href={current.downloadHref}
                            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 font-semibold hover:bg-white/25"
                        >
                            <Download className="h-3 w-3" /> Save
                        </a>
                    )}
                </div>
            )}
            <img
                src={current.src}
                alt={current.alt ?? "Photo"}
                className="max-h-[85vh] max-w-[95vw] rounded-lg object-contain"
                onClick={(e) => e.stopPropagation()}
            />
        </div>,
        document.body,
    );
}
